// What each list section of a resume asks for. The forms are drawn from these, so adding a
// field is one line here (and one in ResumeContent on the server, which decides what is kept).
//
// field types: text (default), textarea, select (with `options`), dates (a start and end, and "I work here now" when
// `current` is set), bullets (a list of short lines), tags (a list of short words).

export const SECTION_LABELS = {
  summary: 'Summary',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
  languages: 'Languages',
}

export const LANGUAGE_LEVELS = ['Native', 'Fluent', 'Advanced', 'Intermediate', 'Basic']

export const LIST_SECTIONS = {
  experience: {
    sorted: true, // kept in date order, the latest first (resumeDates.js)
    noun: 'position',
    title: 'title',
    subtitle: 'company',
    fields: [
      { key: 'title', label: 'Job title', placeholder: 'Senior Software Engineer' },
      { key: 'company', label: 'Company', placeholder: 'Acme Inc.' },
      { key: 'location', label: 'Location', placeholder: 'Berlin, Germany' },
      { type: 'dates', current: true },
      { key: 'description', label: 'Summary', type: 'textarea', rows: 2, placeholder: 'One or two lines on the role and your scope.', wide: true },
      { key: 'bullets', label: 'Achievements', type: 'bullets', placeholder: 'Cut page load time by 40% by ...', wide: true },
    ],
  },
  education: {
    sorted: true,
    noun: 'school',
    title: 'school',
    subtitle: 'degree',
    fields: [
      { key: 'school', label: 'School', placeholder: 'Technical University of Munich' },
      { key: 'degree', label: 'Degree', placeholder: 'BSc' },
      { key: 'field', label: 'Field of study', placeholder: 'Computer Science' },
      { key: 'location', label: 'Location' },
      { type: 'dates' },
      { key: 'grade', label: 'Grade', placeholder: '3.8 GPA' },
      { key: 'description', label: 'Details', type: 'textarea', rows: 2, wide: true },
    ],
  },
  skills: {
    noun: 'skill group',
    title: 'name',
    subtitle: null,
    fields: [
      { key: 'name', label: 'Group name', placeholder: 'Languages', wide: true },
      { key: 'items', label: 'Skills', type: 'tags', placeholder: 'Type a skill and press Enter', wide: true },
    ],
  },
  projects: {
    noun: 'project',
    title: 'name',
    subtitle: 'tech',
    fields: [
      { key: 'name', label: 'Project name' },
      { key: 'url', label: 'Link', placeholder: 'https://' },
      { key: 'tech', label: 'Technologies', placeholder: 'Rails, Vue, PostgreSQL', wide: true },
      { key: 'description', label: 'Summary', type: 'textarea', rows: 2, wide: true },
      { key: 'bullets', label: 'Highlights', type: 'bullets', wide: true },
    ],
  },
  certifications: {
    noun: 'certification',
    title: 'name',
    subtitle: 'issuer',
    fields: [
      { key: 'name', label: 'Name', placeholder: 'AWS Solutions Architect' },
      { key: 'issuer', label: 'Issuer', placeholder: 'Amazon Web Services' },
      { key: 'date', label: 'Date', placeholder: 'Mar 2024' },
      { key: 'url', label: 'Link', placeholder: 'https://' },
    ],
  },
  languages: {
    noun: 'language',
    title: 'name',
    subtitle: 'level',
    fields: [
      { key: 'name', label: 'Language', placeholder: 'German' },
      { key: 'level', label: 'Level', type: 'select', options: LANGUAGE_LEVELS, placeholder: 'Select a level' },
    ],
  },
}

// A section the user names themselves holds generic entries.
export const CUSTOM_SECTION = {
  noun: 'entry',
  title: 'title',
  subtitle: 'subtitle',
  fields: [
    { key: 'title', label: 'Title' },
    { key: 'subtitle', label: 'Subtitle' },
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Details', type: 'textarea', rows: 3, wide: true },
  ],
}

export const newId = () => (globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`)

// A new, empty entry with every field the section asks for.
export function blankEntry(def) {
  const entry = { id: newId() }
  def.fields.forEach((field) => {
    if (field.type === 'dates') {
      entry.start_date = ''
      entry.end_date = ''
      if (field.current) entry.current = false
    } else if (field.type === 'bullets' || field.type === 'tags') {
      entry[field.key] = []
    } else {
      entry[field.key] = ''
    }
  })
  return entry
}

// The words for a section's heading and a section's entries, as the preview and form use them.
export const dateRange = (entry) => {
  const end = entry.current ? 'Present' : entry.end_date
  return [entry.start_date, end].filter(Boolean).join(' – ')
}
